import {
  Auth,
  MultiFactorInfo,
  MultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorInfo,
  RecaptchaVerifier,
} from 'firebase/auth';
import * as React from 'react';
import { getErrorMessage } from '../../../../constants/firebase-auth-errors';
import {
  IFirebaseAppConfig,
  IIdentityTenant,
  IPermission,
  ISignInProvider,
} from '../../../../data/types';
import { LoginState } from '../Login';

/**
 * Return type for the useLoginStateMachine hook
 */
export interface ILoginStateMachineReturns {
  // State values
  email: string;
  password: string;
  error: string;
  verificationCode: string;
  loading: boolean;
  showResendSuccess: boolean;
  resendCooldown: number;
  resetSent: boolean;
  multiFactorHints: MultiFactorInfo[];
  selectedFactor: MultiFactorInfo | null;
  selectedProvider: string | null;
  selectedSSOMethod: string | null;
  selectedOrganization: string | null;
  selectedTenant: string | null;
  loginState: LoginState;

  // State setters
  setEmail: (email: string) => void;
  setPassword: (password: string) => void;
  setError: (error: string) => void;
  setVerificationCode: (code: string) => void;
  setLoading: (loading: boolean) => void;
  setShowResendSuccess: (show: boolean) => void;
  setResendCooldown: (seconds: number) => void;
  setResetSent: (sent: boolean) => void;
  setMultiFactorHints: (hints: MultiFactorInfo[]) => void;
  setSelectedFactor: (factor: MultiFactorInfo | null) => void;
  setSelectedProvider: (provider: string | null) => void;
  setSelectedSSOMethod: (method: string | null) => void;
  setSelectedOrganization: (orgId: string | null) => void;
  setSelectedTenant: (tenantId: string | null) => void;

  // Refs
  auth: React.MutableRefObject<Auth | null>;
  resolver: React.MutableRefObject<MultiFactorResolver | null>;
  verificationId: React.MutableRefObject<string | null>;
  userInfo: React.MutableRefObject<{
    projectId: string;
    sign_in_providers: ISignInProvider[];
    email: string;
    tenant_id?: string | null;
    org_id?: string;
    app_config?: IFirebaseAppConfig;
    tenants?: IIdentityTenant[];
    organizations?: IPermission[];
  } | null>;
  recaptchaVerifier: React.MutableRefObject<RecaptchaVerifier | null>;

  // Functions
  nextState: (state: LoginState) => void;
  sendMultiFactorCode: () => Promise<void>;
}

/**
 * Custom hook to manage login state machine and authentication flow
 * Encapsulates all state management, refs, and state transition logic
 */
export const useLoginStateMachine = (): ILoginStateMachineReturns => {
  // Form state
  const [email, setEmail] = React.useState<string>('');
  const [password, setPassword] = React.useState<string>('');
  const [error, setError] = React.useState<string>('');
  const [verificationCode, setVerificationCode] = React.useState<string>('');
  const [loading, setLoading] = React.useState<boolean>(false);
  const [showResendSuccess, setShowResendSuccess] =
    React.useState<boolean>(false);
  const [resendCooldown, setResendCooldown] = React.useState<number>(0);
  const [resetSent, setResetSent] = React.useState<boolean>(false);

  // Cooldown timer effect
  React.useEffect((): (() => void) | undefined => {
    if (resendCooldown > 0) {
      const timer = setTimeout((): void => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return (): void => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Hide success message after 5 seconds
  React.useEffect((): (() => void) | undefined => {
    if (showResendSuccess) {
      const timer = setTimeout((): void => {
        setShowResendSuccess(false);
      }, 5000);
      return (): void => clearTimeout(timer);
    }
  }, [showResendSuccess]);

  // Multi-factor authentication state
  const [multiFactorHints, setMultiFactorHints] = React.useState<
    MultiFactorInfo[]
  >([]);
  const [selectedFactor, setSelectedFactor] =
    React.useState<MultiFactorInfo | null>(null);

  // Provider selection state
  const [selectedProvider, setSelectedProvider] = React.useState<string | null>(
    null
  );

  // SSO method selection state
  const [selectedSSOMethod, setSelectedSSOMethod] = React.useState<
    string | null
  >(null);

  // Organization selection state
  const [selectedOrganization, setSelectedOrganization] = React.useState<
    string | null
  >(null);

  // Tenant selection state
  const [selectedTenant, setSelectedTenant] = React.useState<string | null>(
    null
  );

  // Login flow state
  const [loginState, setLoginState] = React.useState<LoginState>(
    LoginState.IDENTIFY
  );

  // Refs for authentication data
  const auth = React.useRef<Auth | null>(null);
  const resolver = React.useRef<MultiFactorResolver | null>(null);
  const verificationId = React.useRef<string | null>(null);
  const userInfo = React.useRef<{
    projectId: string;
    sign_in_providers: ISignInProvider[];
    email: string;
    tenant_id?: string | null;
    org_id?: string;
    app_config?: IFirebaseAppConfig;
    tenants?: IIdentityTenant[];
    organizations?: IPermission[];
  } | null>(null);
  const recaptchaVerifier = React.useRef<RecaptchaVerifier | null>(null);

  /**
   * Transition to a new login state with cleanup
   */
  const nextState = React.useCallback((state: LoginState): void => {
    setError('');
    // Clear all state and start fresh if transitioning back to start of flow
    if (state === LoginState.IDENTIFY) {
      setEmail('');
      setPassword('');
      setVerificationCode('');
      setSelectedFactor(null);
      setSelectedProvider(null);
      setSelectedSSOMethod(null);
      setSelectedOrganization(null);
      setSelectedTenant(null);
      auth.current = null;
      resolver.current = null;
      verificationId.current = null;
      userInfo.current = null;
    }
    setLoginState(state);
  }, []);

  /**
   * Send multi-factor authentication code to user
   */
  const sendMultiFactorCode = React.useCallback(async (): Promise<void> => {
    if (!selectedFactor) {
      setError('Please select a verification method.');
      return;
    }

    // Show verifying device state
    nextState(LoginState.VERIFYING_DEVICE_FOR_MFA);

    if (selectedFactor.factorId === 'phone') {
      if (!resolver.current || !auth.current || !recaptchaVerifier.current) {
        setError('Authentication session not initialized.');
        return;
      }

      const phoneInfoOptions = {
        multiFactorHint: selectedFactor as PhoneMultiFactorInfo,
        session: resolver.current.session,
      };
      const phoneAuthProvider = new PhoneAuthProvider(auth.current);
      setLoading(true);

      try {
        verificationId.current = await phoneAuthProvider.verifyPhoneNumber(
          phoneInfoOptions,
          recaptchaVerifier.current
        );
        setLoading(false);
        nextState(LoginState.SECOND_FACTOR_VERIFICATION);
      } catch (err: unknown) {
        const phoneError = err as { code?: string };
        setLoading(false);

        // Check if error is due to delivery failure
        if (
          phoneError.code === 'auth/invalid-phone-number' ||
          phoneError.code === 'auth/missing-phone-number' ||
          phoneError.code === 'auth/quota-exceeded' ||
          phoneError.code === 'auth/captcha-check-failed'
        ) {
          // Show unable to send verification screen
          nextState(LoginState.UNABLE_TO_SEND_VERIFICATION);
        } else {
          // Show generic error
          setError(getErrorMessage(phoneError.code ?? 'unknown'));
        }
        return;
      }
    } else if (selectedFactor.factorId === 'totp') {
      // Simulate brief verification delay for TOTP
      await new Promise<void>((resolve: () => void): void => {
        setTimeout(resolve, 500);
      });
      nextState(LoginState.SECOND_FACTOR_VERIFICATION);
    }
  }, [selectedFactor, nextState]);

  return {
    // State values
    email,
    password,
    error,
    verificationCode,
    loading,
    showResendSuccess,
    resendCooldown,
    resetSent,
    multiFactorHints,
    selectedFactor,
    selectedProvider,
    selectedSSOMethod,
    selectedOrganization,
    selectedTenant,
    loginState,

    // State setters
    setEmail,
    setPassword,
    setError,
    setVerificationCode,
    setLoading,
    setShowResendSuccess,
    setResendCooldown,
    setResetSent,
    setMultiFactorHints,
    setSelectedFactor,
    setSelectedProvider,
    setSelectedSSOMethod,
    setSelectedOrganization,
    setSelectedTenant,

    // Refs
    auth,
    resolver,
    verificationId,
    userInfo,
    recaptchaVerifier,

    // Functions
    nextState,
    sendMultiFactorCode,
  };
};
